// Ticker Data — line composition for the streaming ticker.
//
// The ticker is fed from a Google Sheet, one cell per ticker row. Rows are
// pasted in, so a row is one line of output.
//
// A game recap gets the full line width to itself — that is the default here.
// The gap/pipe padding exists for the other case, several SHORT items sharing a
// line the way rosters do, and reproduces that shape exactly:
//
//   ="#1 Elijah Conjelko QB/SS"&REPT(CHAR(160),12)&"|"&REPT(CHAR(160),12)&"#2 …"
//
// A single full-width item needs no formula, so plain text is the default output
// and the formula form is what you switch to when packing multiple items.
//
// Per-item text is a <token> template, same convention as the Team Scheduler's
// title/description builder: unknown tokens are left as typed so a typo shows
// up in the preview instead of silently blanking.

export const NBSP = '\u00a0'
export const DEFAULT_GAP = 12
// One recap per line by default; raise it to pack short items roster-style.
export const DEFAULT_MAX_ITEMS = 1

// What the ticker is verified to render today, measured on this production line:
//   "Unlimited visibility at 1/2 the cost of broadcast TV ads and 1/3 the cost
//    of premium streaming platform ads! tenband.tv/advertise"           = 129
//
// The budget counts RENDERED characters. With one item per line that is just
// the recap text; when items are packed, the gap padding counts too (a 12-nbsp
// gap either side of a "|" is 25 characters per join). It is an operator
// setting, not a constant, so a longer ceiling can be tested against the real
// crawl without a code change.
export const DEFAULT_CHAR_BUDGET = 129

export const PRESETS = [
  { id: 'recap',    label: 'Recap — both teams', template: '<league> <division-group>: <away-team> <away-score>[ — <away-scorers>]; <home-team> <home-score>[ — <home-scorers>]' },
  { id: 'winner',   label: 'Recap — winner only', template: '<league> <division-group>: <winner> <winner-score>, <loser> <loser-score>[ — <winner-scorers> scored][; <winner-goalie> made <winner-goalie-saves> saves]' },
  { id: 'goalie',   label: 'Recap + top goalie', template: '<away-team> <away-score>[ — <away-scorers>]; <home-team> <home-score>[ — <home-scorers>][ · <top-goalie> (<top-goalie-team>) <top-goalie-saves> saves]' },
  { id: 'score',    label: 'Final score',        template: '<winner> <winner-score>, <loser> <loser-score>' },
  { id: 'division', label: 'Score + division',   template: '<division-group>: <winner> <winner-score>, <loser> <loser-score>' },
  { id: 'matchup',  label: 'Away at home',       template: '<away-team> <away-score> at <home-team> <home-score>' },
  { id: 'live',     label: 'Live score',         template: '<league> <division-group>: <away-team> <away-score>, <home-team> <home-score>[ — <status>]' },
]

// What a game still being played is labelled. Appended automatically when the
// template does not place it itself — see renderResult.
export const IN_PROGRESS_LABEL = 'IN PROGRESS'

export const TOKENS = [
  { key: 'gap',                 label: 'Gap (4 spaces)',    hint: '<gap8> for 8 — non-breaking, survives Sheets' },
  { key: 'status',              label: 'Live status',       hint: 'IN PROGRESS — empty on a finished game' },
  { key: 'winner',              label: 'Winner',            hint: 'Peters Township' },
  { key: 'winner-score',        label: 'Winner score',      hint: '3' },
  { key: 'loser',               label: 'Loser',             hint: 'Greater Latrobe' },
  { key: 'loser-score',         label: 'Loser score',       hint: '1' },
  { key: 'score',               label: 'Score',             hint: '3-1' },
  { key: 'away-team',           label: 'Away team',         hint: 'Peters Township' },
  { key: 'away-score',          label: 'Away score',        hint: '3' },
  { key: 'home-team',           label: 'Home team',         hint: 'Greater Latrobe' },
  { key: 'home-score',          label: 'Home score',        hint: '1' },
  { key: 'winner-scorers',      label: 'Winner goals',      hint: 'Lamagna 2, Meneely' },
  { key: 'loser-scorers',       label: 'Loser goals',       hint: 'Goodlin' },
  { key: 'away-scorers',        label: 'Away goals',        hint: 'Lamagna 2, Meneely' },
  { key: 'home-scorers',        label: 'Home goals',        hint: 'Goodlin' },
  { key: 'winner-goalie',       label: 'Winning goalie',    hint: 'Herman' },
  { key: 'top-goalie',          label: 'Busiest goalie',    hint: 'Sarris' },
  { key: 'top-goalie-team',     label: 'Busiest goalie team', hint: 'Hampton' },
  { key: 'top-goalie-saves',    label: 'Busiest goalie saves', hint: '36' },
  { key: 'winner-goalie-saves', label: 'Winning saves',     hint: '16' },
  { key: 'loser-goalie',        label: 'Losing goalie',     hint: 'Myers' },
  { key: 'loser-goalie-saves',  label: 'Losing saves',      hint: '25' },
  { key: 'away-shots',          label: 'Away shots',        hint: '28' },
  { key: 'home-shots',          label: 'Home shots',        hint: '17' },
  { key: 'division',            label: 'Division',          hint: 'AAA / A Gold' },
  { key: 'division-group',      label: 'Division (short)',  hint: 'AAA' },
  { key: 'league',              label: 'League',            hint: 'PIHL' },
  { key: 'venue',               label: 'Venue',             hint: 'Kirk Nevin Arena' },
  { key: 'date',                label: 'Date',              hint: 'Sep 28' },
  { key: 'date-numeric',        label: 'Date (numeric)',    hint: '9/28' },
  { key: 'weekday',             label: 'Weekday',           hint: 'Monday' },
]

const localDate = (iso) => {
  if (!iso) return null
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d)
}

const surname = (n) => String(n || '').trim().split(/\s+/).pop() || ''

// Token values for one result row from /api/ticker/results.
//
// A tie has no winner: the visitor takes the "winner" slot so templates still
// render in a sane order ("A 2, B 2") rather than dropping half the line.
export function resultContext(g) {
  const d = localDate(g.date)
  const visitorWon = (g.visitor_score ?? 0) >= (g.home_score ?? 0)
  const winScorers  = visitorWon ? g.visitor_scorers : g.home_scorers
  const loseScorers = visitorWon ? g.home_scorers : g.visitor_scorers
  const winGk  = visitorWon ? g.visitor_goalie : g.home_goalie
  const loseGk = visitorWon ? g.home_goalie : g.visitor_goalie
  const list = (arr) => (Array.isArray(arr) ? arr : []).join(', ')
  // Busiest goalie, either side. In a lopsided game the losing keeper is the
  // story — 36 saves in an 8-3 says more than the winner's 14.
  const sides = [{ team: g.visitor, gk: g.visitor_goalie }, { team: g.home, gk: g.home_goalie }]
    .filter(x => x.gk && x.gk.saves != null)
    .sort((a, b) => b.gk.saves - a.gk.saves)
  const topGk = sides[0] || null
  return {
    'status': g.in_progress ? IN_PROGRESS_LABEL : '',
    'winner': visitorWon ? g.visitor : g.home,
    'loser': visitorWon ? g.home : g.visitor,
    'winner-score': String((visitorWon ? g.visitor_score : g.home_score) ?? ''),
    'loser-score': String((visitorWon ? g.home_score : g.visitor_score) ?? ''),
    'score': `${visitorWon ? g.visitor_score : g.home_score}-${visitorWon ? g.home_score : g.visitor_score}`,
    'away-team': g.visitor || '',
    'home-team': g.home || '',
    'away-score': String(g.visitor_score ?? ''),
    'home-score': String(g.home_score ?? ''),
    'winner-scorers': list(winScorers),
    'loser-scorers': list(loseScorers),
    'away-scorers': list(g.visitor_scorers),
    'home-scorers': list(g.home_scorers),
    'top-goalie': surname(topGk?.gk?.name),
    'top-goalie-team': topGk?.team || '',
    'top-goalie-saves': topGk?.gk?.saves == null ? '' : String(topGk.gk.saves),
    'winner-goalie': surname(winGk?.name),
    'winner-goalie-saves': winGk?.saves == null ? '' : String(winGk.saves),
    'loser-goalie': surname(loseGk?.name),
    'loser-goalie-saves': loseGk?.saves == null ? '' : String(loseGk.saves),
    'away-shots': g.visitor_shots == null ? '' : String(g.visitor_shots),
    'home-shots': g.home_shots == null ? '' : String(g.home_shots),
    'division': g.division || '',
    'division-group': g.division_group || g.division || '',
    'league': g.league || '',
    'venue': g.venue || '',
    'date': d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    'date-numeric': d ? `${d.getMonth() + 1}/${d.getDate()}` : '',
    'weekday': d ? d.toLocaleDateString('en-US', { weekday: 'long' }) : '',
  }
}

// Clean up what an empty token leaves behind. A game with no division would
// otherwise read "PIHL : Peters Township 3", and a missing goalie would leave a
// dangling em dash — the operator should not have to keep a separate template
// per combination of present fields.
// Ordinary whitespace only — NBSP (\u00a0) is deliberate spacing the operator
// asked for, and JS's \s would otherwise swallow it.
const WS = '[^\\S\\u00a0]'

function tidy(text) {
  const ws = (flags) => new RegExp(WS + '+', flags)
  return text
    .replace(ws('g'), ' ')                                   // tabs/newlines split cells on paste
    .replace(new RegExp(`${WS}*\\|${WS}*`, 'g'), ' / ')       // a literal pipe reads as a separator
    .replace(new RegExp(`\\(${WS}*[,;·]*${WS}*\\)`, 'g'), '') // "(…)" left empty
    .replace(new RegExp(`${WS}+([,;:.!?])`, 'g'), '$1')       // " :" → ":"
    .replace(new RegExp(`([,;:])${WS}*(?=[,;:])`, 'g'), '')
    .replace(new RegExp(`${WS}*[—–-]${WS}*(?=$|[,;:])`, 'g'), '')
    .replace(new RegExp(`^(?:${WS}|[,;:.—–-])+`), '')
    .replace(new RegExp(`(?:${WS}|[,;:—–-])+$`), '')
    .replace(new RegExp(`${WS}{2,}`, 'g'), ' ')
    .trim()
}

// <token> → value, case-insensitive. An unknown token survives verbatim, so a
// typo is visible in the preview rather than silently blanking.
function substitute(text, ctx) {
  let missing = false
  const out = String(text || '').replace(/<([a-z0-9-]+)>/gi, (whole, key) => {
    const k = key.toLowerCase()
    const gap = /^gap(\d*)$/.exec(k)
    if (gap) return NBSP.repeat(Math.min(Number(gap[1]) || 4, 40))
    if (!Object.prototype.hasOwnProperty.call(ctx, k)) return whole
    const v = ctx[k]
    if (v === '' || v == null) { missing = true; return '' }
    return v
  })
  return { text: out, missing }
}

// A [bracketed] segment is dropped whole when any token inside it has no value.
// Without it a pulled goalie or an unpublished stat leaves "made saves" in the
// crawl, and the operator would need one template per combination of fields.
export function renderTemplate(template, ctx) {
  const resolved = String(template || '').replace(/\[([^\]]*)\]/g, (_whole, inner) => {
    const { text, missing } = substitute(inner, ctx)
    return missing ? '' : text
  })
  return tidy(substitute(resolved, ctx).text)
}

// The one entry point for a game-result line. The IN PROGRESS label is a safety
// property, not a formatting choice: a running score rendered by a template
// written for finals reads on air as a final — "Fox Chapel 5, Hempfield 3" is a
// 1st-period scoreline and a finished game in the same words. So when the
// rendered line does not already carry the label, it is appended.
//
// Checked against the OUTPUT rather than the template, which also covers a
// <status> that sat inside a [bracketed] segment some other empty token dropped.
export function renderResult(template, g) {
  const text = renderTemplate(template, resultContext(g))
  if (!g || !g.in_progress || !text) return text
  return text.includes(IN_PROGRESS_LABEL) ? text : `${text} (${IN_PROGRESS_LABEL})`
}

// Rendered width of a row: the items plus one separator between each pair
// (plus a trailing one when the crawl needs a gap before it loops).
export function separatorLength(gap) {
  return 1 + 2 * Math.max(0, Number(gap) || 0)
}

export function rowLength(items, { gap = DEFAULT_GAP, trailing = false } = {}) {
  const sep = separatorLength(gap)
  const text = items.reduce((n, s) => n + String(s).length, 0)
  const joins = Math.max(0, items.length - 1) + (trailing ? 1 : 0)
  return text + joins * sep
}

// Pack items into rows that stay inside the character budget, order preserved.
// An item that cannot fit a row of its own is still emitted — dropping a game
// silently would be worse than showing it flagged — with `over` set so the UI
// can mark it and the operator can shorten the template or raise the budget.
export function packRows(items, { gap = DEFAULT_GAP, budget = DEFAULT_CHAR_BUDGET,
                                 maxItems = DEFAULT_MAX_ITEMS, trailing = false } = {}) {
  const cap = Math.max(1, Number(maxItems) || 1)
  const limit = Math.max(1, Number(budget) || 1)
  const rows = []
  let current = []
  const flush = () => {
    if (!current.length) return
    const length = rowLength(current, { gap, trailing })
    rows.push({ items: current, length, over: length > limit })
    current = []
  }
  for (const item of items) {
    if (current.length >= cap) flush()
    if (current.length && rowLength([...current, item], { gap, trailing }) > limit) flush()
    current.push(item)
  }
  flush()
  return rows
}

// ="a"&REPT(CHAR(160),12)&"|"&REPT(CHAR(160),12)&"b"
// A quote inside an item must be doubled to survive the Sheets string literal.
export function tickerFormula(items, { gap = DEFAULT_GAP, trailing = false } = {}) {
  const g = Math.max(0, Number(gap) || 0)
  const pad = g > 0 ? `REPT(CHAR(160),${g})` : null
  const sep = pad ? `&${pad}&"|"&${pad}&` : `&"|"&`
  const lit = (s) => String(s)
    .split(/(\u00a0+)/)
    .filter(part => part !== '')
    .map(part => (part[0] === NBSP
      ? `REPT(CHAR(160),${part.length})`
      : `"${part.replace(/"/g, '""')}"`))
    .join('&')
  const body = items.map(lit).join(sep)
  return `=${body}${trailing ? (pad ? `&${pad}&"|"&${pad}` : `&"|"`) : ''}`
}

// The same row as literal text, for anything reading it outside Sheets.
export function tickerPlain(items, { gap = DEFAULT_GAP, trailing = false } = {}) {
  const pad = NBSP.repeat(Math.max(0, Number(gap) || 0))
  const sep = `${pad}|${pad}`
  return items.join(sep) + (trailing ? sep : '')
}

// Cut at a word boundary with an ellipsis, so an over-long recap degrades
// readably instead of being chopped mid-word. Opt-in: silently shortening a
// sentence is the operator's call, not ours.
export function truncateToBudget(text, budget) {
  const limit = Math.max(1, Number(budget) || 1)
  const s = String(text || '')
  if (s.length <= limit) return s
  const cut = s.slice(0, limit - 1)
  const space = cut.lastIndexOf(' ')
  return `${(space > limit * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:—-]+$/, '')}…`
}

export function renderRow(row, { gap, trailing, mode }) {
  return mode === 'plain'
    ? tickerPlain(row.items, { gap, trailing })
    : tickerFormula(row.items, { gap, trailing })
}

export function buildRows(items, opts) {
  return packRows(items, opts).map(row => renderRow(row, opts))
}


// ---------------------------------------------------------------------------
// Standings + leaders feeds
// ---------------------------------------------------------------------------

// Surnames alone collide: Plum dress both Colby and Tyler Bartos, and a
// leaderboard reading "Bartos 2, Bartos 1" looks like a bug on air. Only the
// colliding names get an initial, so the common case stays short.
export function displayNames(fullNames) {
  const counts = new Map()
  for (const n of fullNames) {
    const last = String(n || '').trim().split(/\s+/).pop() || ''
    counts.set(last, (counts.get(last) || 0) + 1)
  }
  return fullNames.map(n => {
    const parts = String(n || '').trim().split(/\s+/)
    const last = parts.pop() || ''
    if ((counts.get(last) || 0) < 2 || !parts.length) return last
    return `${parts[0][0]}. ${last}`
  })
}

// W-L-OTW-OTL, kept apart. Summing the overtime columns loses what separates
// two teams: Pine-Richland 3-0-1-0 (11 pts) and North Allegheny 3-0-0-1 (10 pts)
// both collapse to "3-0-1" while sitting in different places in the table.
const record = (t) => `${t.w ?? 0}-${t.l ?? 0}-${t.otw ?? 0}-${t.otl ?? 0}`

export const STANDINGS_TOKENS = [
  { key: 'league',          label: 'League',            hint: 'PIHL' },
  { key: 'division',        label: 'Division',          hint: 'AAA / A Gold' },
  { key: 'division-group',  label: 'Division (short)',  hint: 'AAA' },
  { key: 'standings',       label: 'Standings list',    hint: 'SV 2-0-1-1 · PT 4-0-0-0' },
  { key: 'standings-full',  label: 'Code + record + pts', hint: 'SV 2-0-1-1 (9) · PT 4-0-0-0 (12)' },
  { key: 'standings-long-full', label: 'Name + record + pts', hint: 'Seneca Valley 2-0-1-1 (9)' },
  { key: 'standings-long',  label: 'Standings (names)', hint: 'Seneca Valley 1-0-0' },
  { key: 'standings-pts',   label: 'Standings by pts',  hint: 'SV 3 · PT 3' },
  { key: 'leader',          label: 'Division leader',   hint: 'Seneca Valley' },
  { key: 'leader-code',     label: 'Leader (code)',     hint: 'SV' },
  { key: 'leader-record',   label: 'Leader record',     hint: '4-0-0-0' },
  { key: 'shown',           label: 'Teams shown',       hint: '5' },
  { key: 'total',           label: 'Teams in division', hint: '11' },
  { key: 'gap',             label: 'Gap (4 spaces)',    hint: '<gap8> for 8' },
]

export function standingsContext(div, { league = '' } = {}) {
  const teams = div.teams || []
  const first = teams[0] || {}
  return {
    'league': league,
    'division': String(div.division || '').replace(/^Class /, ''),
    'division-group': div.division_group || '',
    'standings': teams.map(t => `${t.team_code} ${record(t)}`).join(' · '),
    'standings-full': teams.map(t => `${t.team_code} ${record(t)} (${t.pts ?? 0})`).join(' · '),
    'standings-long-full': teams.map(t => `${t.team_name} ${record(t)} (${t.pts ?? 0})`).join(' · '),
    'standings-long': teams.map(t => `${t.team_name} ${record(t)}`).join(' · '),
    'standings-pts': teams.map(t => `${t.team_code} ${t.pts ?? 0}`).join(' · '),
    'leader': first.team_name || '',
    'leader-code': first.team_code || '',
    'leader-record': teams.length ? record(first) : '',
    'shown': String(teams.length),
    'total': String(div.total_teams ?? teams.length),
  }
}

export const STANDINGS_PRESETS = [
  { id: 'full',   label: 'Codes + record + pts', template: '<league> <division>: <standings-full>' },
  { id: 'codes',  label: 'Codes + record',  template: '<league> <division>: <standings>' },
  { id: 'names',  label: 'Names + record',  template: '<league> <division>: <standings-long>' },
  { id: 'names-full', label: 'Names + record + pts', template: '<league> <division>: <standings-long-full>' },
  { id: 'points', label: 'Codes + points',  template: '<league> <division> points: <standings-pts>' },
  { id: 'leader', label: 'Leader only',     template: '<league> <division> leader: <leader> <leader-record>' },
]

const METRIC_WORD = { goals: 'goals', assists: 'assists', points: 'points' }

export const LEADERS_TOKENS = [
  { key: 'league',         label: 'League',           hint: 'PIHL' },
  { key: 'division-group', label: 'Division',         hint: 'AAA' },
  { key: 'metric',         label: 'Metric',           hint: 'goals' },
  { key: 'leaders',        label: 'Leader list',      hint: 'Wilhelm 5, Norkus 5' },
  { key: 'leaders-team',   label: 'Leaders + team',   hint: 'Wilhelm (VARDL) 5' },
  { key: 'top',            label: 'Top player',       hint: 'Wilhelm' },
  { key: 'top-value',      label: 'Top value',        hint: '5' },
  { key: 'top-team',       label: 'Top player team',  hint: 'VARDL' },
  { key: 'gap',            label: 'Gap (4 spaces)',   hint: '<gap8> for 8' },
]

export function leadersContext(group, { league = '', metric = 'points' } = {}) {
  const players = group.players || []
  const names = displayNames(players.map(p => p.player_name))
  const first = players[0] || {}
  return {
    'league': league,
    'division-group': group.division_group || '',
    'metric': METRIC_WORD[metric] || metric,
    'leaders': players.map((p, i) => `${names[i]} ${p.value}`).join(', '),
    'leaders-team': players.map((p, i) => `${names[i]} (${p.team_code}) ${p.value}`).join(', '),
    'top': names[0] || '',
    'top-value': first.value == null ? '' : String(first.value),
    'top-team': first.team_code || '',
  }
}

export const LEADERS_PRESETS = [
  { id: 'list',   label: 'Leader list',      template: '<league> <division-group> <metric>: <leaders>' },
  { id: 'team',   label: 'List + team',      template: '<league> <division-group> <metric>: <leaders-team>' },
  { id: 'top',    label: 'Top scorer only',  template: '<league> <division-group> <metric> leader: <top> (<top-team>) <top-value>' },
]
